import { MediaItem } from '@modules/Nzo.CMS/Frontend/libraries/cmsPage/blocks/blocks.state'
import { MediaGalleryItemState } from '@modules/Nzo.CMS/Frontend/libraries/cmsPage/blocks/mediaGalleryBlock/mediaGalleryBlock.state'
import { MediaGalleryBlockView } from '@modules/Nzo.CMS/Frontend/libraries/cmsPage/blocks/mediaGalleryBlock/mediaGalleryBlock.template'
import { Img } from '@shared/Frontend/components/img'
import { Lightbox, LightboxTrigger } from '@shared/Frontend/components/lightbox'
import { useInView } from '@shared/Frontend/hooks/useInView'
import { cn } from '@shared/Frontend/utils/addToClassName'
import { parseMediaVideoFile } from '@shared/Frontend/utils/parseMedia'
import { Maybe } from 'ballerina-core'
import React, { MutableRefObject, useId, useRef } from 'react'
import { BlockActions } from '../shared/blockActions'
import { BlockHeader } from '../shared/blockHeader'

export const MediaGalleryBlock: MediaGalleryBlockView['MediaGalleryBlock'] = ({
  items,
  id,
  onOpen,
  onPrev,
  onNext,
  ...props
}) => {
  if (items.length == 0) return <></>
  const { inView, ref } = useInView({ threshold: 0.1 })
  const lightboxId = useId()

  const thumbnailItems = items.slice(0, Math.min(items.length, 5))

  const lightboxedItem = items.find((item) => item.isForeground)
  const lightboxedItemIndex = items.findIndex((item) => item.isForeground)

  const lightboxTriggerRef = useRef<Maybe<HTMLButtonElement>>(undefined)
  return (
    <section id={id} ref={ref} className={cn('block block--gallery ls-spacing', inView && 'inview')}>
      <div className="block__inner ls-container ls-gutter">
        <BlockHeader {...props} />

        <div className="block__content">
          <ListLayout
            lightboxId={lightboxId}
            items={thumbnailItems}
            lightboxedItem={lightboxedItem}
            lightboxTriggerRef={lightboxTriggerRef}
            onOpen={(index) => onOpen(index)}
            listClassName="block__list"
          />

          {lightboxedItem && lightboxTriggerRef.current && (
            <Lightbox
              {...props}
              {...lightboxedItem}
              key={lightboxedItemIndex} //TODO: when lightbox works without remounting, remove key
              lightboxTrigger={lightboxTriggerRef.current}
              index={lightboxedItemIndex}
              id={lightboxId}
              totalCount={items.length}
              onPrev={() => onPrev(lightboxedItemIndex)}
              onNext={() => onNext(lightboxedItemIndex)}
            />
          )}
        </div>

        <BlockActions {...props} datalayerCategory="gallery_block" />
      </div>
    </section>
  )
}

const ListLayout = ({
  items,
  lightboxId,
  lightboxedItem,
  listClassName,
  onOpen,
  lightboxTriggerRef,
}: {
  items: Array<MediaGalleryItemState>
  lightboxId: string
  lightboxedItem?: MediaGalleryItemState
  listClassName?: string
  onOpen: (index: number) => void
  lightboxTriggerRef: MutableRefObject<Maybe<HTMLButtonElement>>
}) => (
  <ul className={cn('gallery', `gallery--${items.length}`, listClassName)}>
    {items.map((media, index) => (
      <ItemLayout
        {...media}
        key={index}
        id={lightboxId}
        isForeground={lightboxedItem?.isForeground || false}
        onOpen={() => onOpen(index)}
        remainingMediaCount={
          index + 1 >= items.length && items.length - items.length > 0 ? items.length - items.length : undefined
        }
        lightboxTriggerRef={lightboxTriggerRef}
      />
    ))}
  </ul>
)

const ItemLayout = ({
  id,
  isForeground,
  remainingMediaCount,
  onOpen,
  lightboxTriggerRef,
  ...media
}: MediaItem & {
  id: string
  isForeground: boolean
  remainingMediaCount?: number
  onOpen: () => void
  lightboxTriggerRef: MutableRefObject<Maybe<HTMLButtonElement>>
}) => {
  const parsedVideo = media.kind === 'video' && media.url ? parseMediaVideoFile(media.url) : undefined
  const videoID = (parsedVideo?.provider == 'youtube' && parsedVideo?.id) || undefined
  return (
    <li className={`gallery__item gallery__item--${media.kind}`}>
      <LightboxTrigger
        lightboxTriggerRef={lightboxTriggerRef}
        id={id}
        isForeground={isForeground}
        onOpen={onOpen}
        className="gallery__visual"
      >
        {media.kind === 'image' ? (
          <Img
            {...media.image}
            loading="lazy"
            className="gallery__image"
            // 16:9 to reserve some space
            width="800"
            height="450"
          />
        ) : (
          media.kind == 'video' &&
          videoID && (
            <Img
              alt={media.kind}
              className="gallery__image"
              // Get video thumbnail (YouTube only)
              src={`https://i.ytimg.com/vi/${videoID}/maxresdefault.jpg`}
              crops={[{ url: `https://i.ytimg.com/vi/${videoID}/maxresdefault.jpg`, width: 1280 }]}
              width="800"
              height="450"
            />
          )
        )}

        {remainingMediaCount ? (
          <p className="gallery__count">
            <span className="gallery__count-number">{`+${remainingMediaCount}`}</span>
            <span className="gallery__count-text sr-text">Show all</span>
          </p>
        ) : (
          <span className="sr-text">View enlarged</span>
        )}
      </LightboxTrigger>
    </li>
  )
}
